import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useDesignVersionToggle } from '@/hooks'

import Dashboard from '../Dashboard'
import {
  selectActiveBuilding,
  selectBuildingsForFilter,
} from '../../reducers/buildingSelectorReducer'
import { selectSurveyHistory, getSurveyHistory } from '../../reducers/surveyReducer'
import { selectUserRole } from '../../reducers/userDataReducer'
import ConfirmPopup from '../Popups/ConfirmPopup'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import PaginationFooter from '../PaginationFooter/PaginationFooter'
import API from '../../api/api'

import SurveyHeader from './SurveyHeader'
import SurveyNavbar from './SurveyNavbar'
import SurveyTable from './SurveyTable'
import SurveyAnalytics from './SurveyAnalytics'
import { getSurveyStatuses, getSurveyType } from './helpers'

const Surveys = () => {
  const { t } = useTranslation(['surveys', 'common'])
  const surveyStatuses = getSurveyStatuses(t)
  const surveyType = getSurveyType(t)
  const { surveyId } = useParams()
  const surveyHistory = useSelector(selectSurveyHistory)
  const selectedBuilding = useSelector(selectActiveBuilding)
  const buildings = useSelector(selectBuildingsForFilter)
  const userRole = useSelector(selectUserRole)
  const dispatch = useDispatch()

  const [isLoading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [surveyToDelete, setSurveyToDelete] = useState(null)
  const [messageStatusFilter, setMessageStatusFilter] = useState(surveyStatuses[0])
  const [analyticsOpen, setAnalyticsOpen] = useState(true)

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'surveys',
    screen: 'Surveys',
    to: { path: '/dashboard/survey' },
    version: 'legacy',
  })

  const { displaySnackbar } = useSnackbar()

  const refreshSurveyHistoryTable = async (showStatus) => {
    setLoading(true)
    const responseData = await dispatch(
      getSurveyHistory(1, selectedBuilding.id, search, showOnPage, surveyType, messageStatusFilter),
    )
    setPaginationItemsCount(responseData.count)
    setPageNumber(1)
    if (showStatus) displaySnackbar(t('surveys:table.messageSaved'))
  }

  useEffect(() => {
    selectedBuilding && refreshSurveyHistoryTable()
  }, [selectedBuilding, showOnPage, messageStatusFilter])

  useEffect(() => {
    ;(async () => {
      setLoading(true)

      if (selectedBuilding && selectedBuilding.id) {
        const responseData = await dispatch(
          getSurveyHistory(1, selectedBuilding.id, '', 50, surveyType, messageStatusFilter),
        )
        setPaginationItemsCount(responseData.count)
      }
      setLoading(false)
    })()
  }, [selectedBuilding])

  const filteredHistoryList = useMemo(() => {
    return surveyHistory
  }, [surveyHistory, pageNumber])

  useEffect(() => {
    setLoading(false)
  }, [filteredHistoryList])

  const handleDeleteSyrvey = async () => {
    try {
      surveyToDelete && (await API.deleteSurvey(surveyToDelete.id))
      refreshSurveyHistoryTable()
      displaySnackbar(t('surveys:table.successDelete'))
    } catch {
      displaySnackbar(t('surveys:table.errorDelete'), true)
    } finally {
      setSurveyToDelete(null)
    }
  }

  const toggleAnalytics = () => setAnalyticsOpen((prev) => !prev)

  const changeTab = (newTab) => {
    if (newTab !== messageStatusFilter) {
      setMessageStatusFilter(newTab)
      setLoading(true)
    }
  }

  const handleSearch = (searchValue) => {
    setSearch(searchValue)
    setPageNumber(1)
  }

  const maxTableHeight = {
    maxHeight: `calc(100vh - ${analyticsOpen ? 550 : 300}px)`,
  }

  if (redirect) return redirect

  return (
    <Dashboard.Container className={`ctp-survey__wrapper ${isLoading ? 'is-loading' : ''}`}>
      <SurveyHeader
        analyticsOpen={analyticsOpen}
        onUIToggle={handleUIToggle}
        toggleAnalytics={toggleAnalytics}
        selectedBuilding={selectedBuilding}
        refreshTable={() => refreshSurveyHistoryTable()}
        buildings={buildings}
        userRole={userRole}
      />
      {analyticsOpen && <SurveyAnalytics buildingId={selectedBuilding && selectedBuilding.id} />}
      <div className="ctp-survey__box">
        <SurveyNavbar
          changeTab={changeTab}
          selectedTab={messageStatusFilter}
          search={search}
          handleSearch={handleSearch}
        />
        <SurveyTable
          buildings={buildings}
          isSiteLoading={isLoading}
          selectedSurveyId={surveyId}
          searchValue={search}
          buildingId={selectedBuilding && selectedBuilding.id}
          selectedBuilding={selectedBuilding}
          showOnPage={showOnPage}
          pageNumber={pageNumber}
          data={filteredHistoryList}
          tableTypeFilters={surveyType}
          tableStatusFilter={messageStatusFilter}
          setPaginationItemsCount={setPaginationItemsCount}
          setSurveyToDelete={setSurveyToDelete}
          refreshTable={() => refreshSurveyHistoryTable()}
          maxTableHeight={maxTableHeight}
          userRole={userRole}
        />
        <div className="ctp-survey__pagination">
          {surveyHistory && surveyHistory.length ? (
            <PaginationFooter
              showOnPage={showOnPage}
              pageNumber={pageNumber}
              paginationItemsCount={paginationItemsCount}
              setSiteLoading={setLoading}
              setShowOnPage={setShowOnPage}
              setPageNumber={setPageNumber}
            />
          ) : null}
        </div>
      </div>
      <ConfirmPopup
        title={t('surveys:table.confirmDeleteTitle')}
        body={t('surveys:table.confirmDeleteBody', {
          name: surveyToDelete && surveyToDelete.name,
        })}
        cancelText={t('common:actions.cancel')}
        confirmText={t('surveys:table.confirmDeleteYes')}
        isOpen={!!surveyToDelete}
        cancelCallback={() => setSurveyToDelete(null)}
        confirmCallback={() => handleDeleteSyrvey()}
      />
    </Dashboard.Container>
  )
}

export default Surveys
